'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

const [DFS, BFS] = [1, 2];
/**
 * It takes a target object, a visitor function, and a search method (defaulting to depth first
 * search), and returns the result of the search
 * @param {any} target - The object to traverse.
 * @param {visitor} visitor - A function that will be called on each node in the tree.
 * @param searchMethod - The search method to use. Can be either DFS or BFS.
 * @param maxDepth - The maximum depth to search.
 * @returns The result of the search.
 */
function r(target, visitor, searchMethod = DFS, maxDepth = Infinity) {
    if (searchMethod === BFS) {
        return breadthFirstSearch(target, visitor, maxDepth);
    }
    return depthFirstSearch(target, visitor, maxDepth);
}
/**
 * DFS version of r
 */
function depthFirstSearch(target, visitor, maxDepth = Infinity) {
    return (function run(node, depth = 0) {
        if (depth > maxDepth) {
            return target;
        }
        if (node && typeof node === 'object') {
            for (let prop in node) {
                let subNode = node[prop];
                subNode = visitor.call(node, prop, subNode, depth);
                run(subNode, depth + 1);
            }
            return target;
        }
    })(target);
}
/**
 * BFS version of r
 */
/**
 * It takes a target object, a visitor function, and a maximum depth, and then calls the visitor
 * function on every property of the target object and its children, up to the maximum depth
 * @param {any} target - The object to traverse.
 * @param {visitor} visitor - a function that takes the following parameters:
 * @param maxDepth - The maximum depth to traverse.
 */
function breadthFirstSearch(target, visitor, maxDepth = Infinity) {
    const queue = [[target]];
    let depth = 1;
    // 每次循环一个深度(depth)
    while (queue.length > 0) {
        const layer = queue.shift();
        depth++;
        // 处理当前深度下的所有节点
        for (let depth = 0, ll = layer.length; depth < ll; depth++) {
            const node = layer[depth];
            if (typeof node === 'object') {
                const newLayer = [];
                // 处理当前节点下的所有属性
                for (let prop in node) {
                    if (Object.hasOwnProperty.call(node, prop)) {
                        const value = node[prop];
                        const nv = visitor.call(node, prop, value, depth);
                        newLayer.push(nv ?? value);
                    }
                }
                queue.push(newLayer);
            }
        }
        if (depth > maxDepth) {
            break;
        }
    }
    return target;
}

/**
 * JS数据处理工具函数库，不涉及语言之外的API
 */
const esug = `
console.error(e);
if (e instanceof ReferenceError) {
    console.warn('Did you forget to specify some params?');
}
`;
/**
 * It takes a string and an object, and returns a function that executes the string with the object's
 * properties as variables
 * @param {string} valueExpression - The expression to be evaluated.
 * @param {Obj} args - The object that contains the variables that will be used in the expression.
 * @returns A function that returns the value of the expression.
 */
function execfn(valueExpression, args) {
    try {
        const vars = Object.entries(args ?? {}).map(([key, value]) => {
            return `${key} = ${JSON.stringify(value)}`;
        }).join(',');
        const exp = `return (${valueExpression});`;
        return new Function(vars, `try {${exp}} catch(e) {${esug}; return arguments[0]}`);
    }
    catch (error) {
        console.log('Exec(fn) error:', ` \`${valueExpression}\` `, error);
        return noop;
    }
}
/**
 * Execute a string as Javascript expression and get its result value
 * @param valueExpression 任意JS表达式
 * @param host An object that provides local vars which could be used in valueExpression
 * @returns 传入表达式的返回值
 */
function exec(valueExpression, host = null) {
    try {
        return execfn(valueExpression, host).call(host);
    }
    catch (error) {
        console.log('Exec error:', ` \`${valueExpression}\` `, error);
    }
}
/**
 * Deep clone (using JSON.stringify then JSON.parse)
 * @param value
 * @returns
 */
function clone(value) {
    return JSON.parse(JSON.stringify(value));
}
/**
 * "noop takes an optional argument of type T and returns a value of type T."
 *
 * The type of the argument is optional, so we can call noop with no argument:
 *
 * noop(); // no error
 * noop(undefined); // no error
 * @param {T} [arg] - The argument to return.
 * @returns the argument that was passed in.
 */
function noop(arg) {
    return arg;
}
/**
 * Merge two or more (if provide) data objs into a new one, remove data entries which 'key' not in the first argument 'keys' array
 * @param keys The keys to preserve in the merged data
 * @param target There must be atleast one target to process
 * @param otherTargets More target to merge
 * @returns
 */
function pick(keys, target, ...otherTargets) {
    const data = { ...target };
    Object.assign.apply(null, [data, ...otherTargets]);
    return keys.reduce((merged, key) => {
        if (key in data) {
            merged[key] = data[key];
        }
        return merged;
    }, {});
}
/**
 * 它接受一个目标对象和一个修改器对象列表，并返回一个新对象，该对象是将修改器对象合并到目标对象中的结果
 * @param {data} target - 数据
 * @param modifiers - 大批<data>
 * @returns 具有与目标对象相同的键的新对象，但具有来自修改器的值。
 */
function update(target, ...modifiers) {
    return pick(Object.keys(target), target, ...modifiers);
}
function createCallableObject(fn, ...protos) {
    return defaults((...args) => fn(...args), ...protos);
}
function defaults(target, ...sources) {
    const os = Object.assign({}, ...sources);
    return Object.entries(os).reduce((tar, [key, value]) => {
        if (typeof tar[key] === 'undefined') {
            tar[key] = value;
        }
        return tar;
    }, target);
}

/**
 * A task object is used by TaskChain as a single processing node
 */
class Task {
    bindedArgs;
    name;
    /**
     *
     * @param tah A Task object or TaskHandler function
     * @param options TaskOptions
     */
    constructor(tah, options = { bindedArgs: [] }) {
        if (typeof tah === 'function') {
            this.exec = tah;
            this.name = tah.name ?? new.target.name;
        }
        else {
            this.name = tah;
        }
        if (typeof options.exec === 'function') {
            this.exec = options.exec;
        }
        this.bindedArgs = options.bindedArgs || [];
    }
    /**
     *
     * @param value Execute this task and pass the current value to it
     * @param args
     * @returns
     */
    exec(value, ...args) {
        return noop(value);
    }
    /**
     *
     * @param value
     * @returns
     */
    apply(value) {
        return this.exec.apply(this, [value, ...this.bindedArgs]);
    }
    // TODO support params: tn(a,b)
    toString() {
        return this.name;
    }
}
function defaultBreakIf(value, index) {
    return typeof value === 'undefined';
}
class TaskChain {
    tasks;
    value;
    breakif = defaultBreakIf;
    constructor(tasks, options = {}) {
        this.tasks = tasks.map(task => {
            if (task instanceof Task) {
                return task;
            }
            return new (class extends Task {
                exec = task;
            })(task.name);
        });
        if (typeof options.breakif === 'function') {
            this.breakif = options.breakif;
        }
    }
    get length() {
        return this.tasks.length;
    }
    /**
     * Get an array of task names in the chain
     * Non-name tasks will be ignored
     * @returns task name list
     */
    get names() {
        return this.tasks.filter(task => !!task.name).map(task => task.name);
    }
    add(handler, ...bindedArgs) {
        this.tasks.push(new Task(handler.name, { bindedArgs }));
        // const res = processor.apply(this, [this.value, ...args]);
        // this.value = res;
        return this;
    }
    async exec(value) {
        let { tasks } = this;
        let task;
        let i = 0;
        const taskCount = tasks.length;
        while ((taskCount > i)) {
            task = tasks[i];
            // console.log(`Running task: #${i}: ${task.name}`);
            value = await task.apply(value);
            // break if task returns undefined
            if (this.breakif(value, i)) {
                break;
            }
            // if (i < 0) {
            //     break;
            // }
            i++;
        }
        return value;
    }
    async all(value) {
        return (await Promise.all(this.tasks.map(task => task.apply(value)))).every((res, i, a) => res === a[0]);
    }
    toString() {
        return this.names.join(',');
    }
}

/**
 * Stack store data as FILO(first in last out)
 * @author chengxiao
 */
class Stack {
    arr = [];
    constructor(...ini) {
        this.push(...ini);
    }
    get length() {
        return this.arr.length;
    }
    /**
     * Append each argument in to stack
     * @param values argument list that will be append to this stack
     * @returns
     */
    push(...values) {
        // console.log(values)
        return this.arr.push(...values);
    }
    /**
     * Remove and get the element on top of stack
     * @returns the top element in stack
     */
    pop() {
        return this.arr.pop();
    }
    /**
     * Get the element on top of stack
     * @returns the last pushed element or if stack is empty, undefined
     */
    get top() {
        const len = this.arr.length;
        if (len > 0) {
            return this.arr[len - 1];
        }
        return undefined;
    }
    get bottom() {
        return this.arr[0];
    }
    /**
     * Remove all elements in stack
     * @returns this
     */
    clear() {
        this.arr.length = 0;
        return this;
    }
    [Symbol.iterator] = function* () {
        let i = 0;
        while (i < this.arr.length) {
            yield this.arr[i++];
        }
        return null;
    };
}

class Vacancy {
    raw;
    constructor(value) {
        this.raw = value;
    }
}
class ConditionVacancy extends Vacancy {
    constructor(exp) {
        super(exp);
    }
    hasElse = false;
    test(data) {
        return !!exec(this.raw, data);
    }
}
function IF(exp) {
    return new ConditionVacancy(exp);
}
function ID(id) {
    return new IdVacancy(id);
}
const ENDIF = Symbol('endif');
const ELSE = Symbol('else');
class IdVacancy extends Vacancy {
    constructor(id) {
        super(id);
    }
    toString() {
        return this.raw;
    }
}

class GlobalCond extends ConditionVacancy {
    constructor(exp) {
        super(exp);
    }
    test(exp) {
        return true;
    }
}
const globalCond = new GlobalCond('');
const COLON = ':';
/**
 * It takes a template string and a list of values, and returns a factory function that takes a data object
 * and returns formatted string
 * @param {TemplateStringsArray} tsa - TemplateStringsArray
 * @param vcs - Array<Vacancy | Symbol | string | number>
 * @returns A function that takes a data object and returns a string.
 * @example const fac = TPL`abc${ID('k1')}efg${ID('k2')}ijk`;
 * // Using named data
 * fac({k: 'd', k1: 'h'});
 *
 * // Using indexed data
 * fac('d', 'h');
 */
function TPL(tsa, ...vcs) {
    return (data, ...valueArr) => {
        let values = {};
        if (typeof data === 'string') {
            Object.assign(values, [data, ...valueArr]);
        }
        else {
            Object.assign(values, data);
        }
        const condStack = new Stack();
        const resArr = tsa.map((ts, ind, { length }) => {
            const vc = vcs[ind];
            const tss = JSON.stringify(ts);
            const cond = condStack.top || globalCond;
            if (typeof vc === 'string') {
                return JSON.stringify(`${ts}${vc}`) + '+';
            }
            if (typeof vc === 'number') {
                return JSON.stringify(`${ts}${values[vc]}`) + '+';
            }
            if (vc instanceof IdVacancy) {
                return JSON.stringify(ts + (values[String(vc)] ?? values[ind] ?? '')) + '+';
            }
            // IF(...)
            if (vc instanceof ConditionVacancy) {
                const tv = vc.test(values);
                condStack.push(vc);
                return tss + '+(' + tv + '?';
            }
            if (vc === ELSE) {
                if (cond === globalCond) {
                    throw new SyntaxError('Unexpected Vacancy: ELSE');
                }
                cond.hasElse = true;
                return `${tss}${COLON}`;
            }
            if (vc === ENDIF) {
                if (cond === globalCond) {
                    throw new SyntaxError('Unexpected Vacancy: ENDIF');
                }
                let colon = '';
                if (!cond.hasElse) {
                    colon = `${COLON}""`;
                }
                condStack.pop();
                return tss + colon + ')+';
            }
            if (ind === length - 1) {
                return tss;
            }
            return `${tss}+`;
        });
        //          console.log(resArr);
        return exec(resArr.join(''));
    };
}
/**
 * Generate a map of ids
 * @param keys args of string, whitch will be used as keys of id
 * @returns `{
 *      key: ID(key)
 * }`
 */
function genIdSym(...keys) {
    return Object.fromEntries([...function* (...k) {
            let key;
            while ((key = keys.pop())) {
                yield [key, ID(key)];
            }
        }(keys.slice())]);
}

exports.BFS = BFS;
exports.ConditionVacancy = ConditionVacancy;
exports.DFS = DFS;
exports.ELSE = ELSE;
exports.ENDIF = ENDIF;
exports.ID = ID;
exports.IF = IF;
exports.IdVacancy = IdVacancy;
exports.Stack = Stack;
exports.TPL = TPL;
exports.Task = Task;
exports.TaskChain = TaskChain;
exports.Vacancy = Vacancy;
exports.clone = clone;
exports.createCallableObject = createCallableObject;
exports.defaults = defaults;
exports.exec = exec;
exports.execfn = execfn;
exports.genIdSym = genIdSym;
exports.noop = noop;
exports.pick = pick;
exports.r = r;
exports.update = update;
